import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { get } from 'lodash';
import { format } from 'date-fns';

import {
  VerificationStatus,
  UserRole,
  getErrorMessage,
} from 'utils/bankStatements';
import { getOrConvertToPst } from 'utils/timezone';

import InlineTransactionForm from './InlineTransactionForm';
import { useToast } from './ui/toast';
import { TransactionEditor } from './TransactionEditor';
import AccountHeader from './AccountHeader';
import TransactionActions from './TransactionActions';

const validateDate = (date) => {
  if (!date || date.trim() === '') return 'Date is required';
  const inputDate = new Date(date);
  if (Number.isNaN(inputDate.getTime())) return 'Invalid date format';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (inputDate > today) return 'Cannot enter future dates';
  return null;
};

const validateAmount = (amount) => {
  if (!amount || amount.trim() === '') return 'Amount is required';
  const value = parseFloat(amount);
  if (Number.isNaN(value)) return 'Invalid amount';
  if (value < 0) return 'Amount cannot be negative';
  return null;
};

const headerStyles = {
  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    backgroundColor: '#F9FAFB',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    height: '36px',
  },
};

export const TransactionsTable = ({
  accounts,
  statementId,
  selectedStatement,
  onTransactionSelect,
  selectedTransactionId,
  selectedAccountId,
  onTransactionUpdate,
  onTransactionDelete,
  onAccountUpdate,
  onStatementUpdate,
  onTransactionAdd,
  transactionCounts,
  showNeedsReview,
  showVerified,
  showManuallyVerified,
  onBack,
}) => {
  const [editingTransactionMeta, setEditingTransactionMeta] = useState(null);
  const [editingAccountMeta, setEditingAccountMeta] = useState(null);
  const [savingFields, setSavingFields] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const { showToast } = useToast();
  const [addingInlineToAccountId, setAddingInlineToAccountId] = useState(null);
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Split the date string and reconstruct it to avoid timezone issues
    const [year, month, day] = dateString.split('-');
    if (!year || !month || !day) return dateString;
    return `${month}/${day}/${year}`;
  };

  const handleAccountEdit = (accountId, field) => {
    setEditingAccountMeta({ accountId, field });
    // Disable transaction selection during edit
    if (selectedTransactionId) {
      const account = accounts.find(a => a.id === accountId);
      if (account) {
        onTransactionSelect?.(0, account.accountNumber);
      }
    }
  };

  const handleAddTransactionClick = (accountId) => {
    console.log('TransactionsTable: Add Transaction clicked', { accountId }); // Debug log
    setAddingInlineToAccountId(accountId);
    console.log('Setting addingInlineToAccountId to:', accountId); // New debug log
  };

  const userRole = useSelector((state) => get(state, 'bankStatements.settings.userRole'));
  const [hoveredRow, setHoveredRow] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  // Add padding to table container to prevent overlap with sticky message
  useEffect(() => {
    const container = document.querySelector('.table-container');
    if (
      container
      && userRole === UserRole.User
      && transactionCounts.needsReview === 0
    ) {
      container.style.paddingBottom = '80px';
    } else if (container) {
      container.style.paddingBottom = '0';
    }
  }, [userRole, transactionCounts.needsReview]);

  useEffect(() => () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  }, []);

  // Transaction editing handlers
  const startEdit = (e, transactionId, accountId, field) => {
    e.stopPropagation();
    setEditingTransactionMeta({ accountId, transactionId, field });
  };

  const cancelEdit = () => {
    setEditingTransactionMeta(null);
  };

  const clearTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setHoveredRow(null);
  };

  const handleTransactionSave = async (updatedTransaction) => {
    try {
      const apiPayload = {
        ...updatedTransaction,
        depositAmount: updatedTransaction.type === 'Deposit' ? updatedTransaction.depositAmount : undefined,
        withdrawalAmount: updatedTransaction.type === 'Withdrawal' ? updatedTransaction.withdrawalAmount : undefined,
        date: format(getOrConvertToPst(updatedTransaction.date), 'yyyy-MM-dd'),
        verificationStatus: VerificationStatus.Manual,
        lastVerifiedAt: new Date().toISOString(),
        dateConfidence: 1.0,
        descriptionConfidence: 1.0,
        depositAmountConfidence: updatedTransaction.depositAmount
          ? 1.0
          : undefined,
        withdrawalAmountConfidence: updatedTransaction.withdrawalAmount
          ? 1.0
          : undefined,
      };

      await onTransactionUpdate?.(apiPayload, false);
      cancelEdit();
      clearTooltip();
    } catch (error) {
      console.error('Error saving transaction:', error);
      const errorMsg = getErrorMessage(error, 'Failed to update transaction');
      showToast(errorMsg, 'error');
    }
  };

  const handleTransactionDelete = async (transactionId) => {
    try {
      if (!selectedStatement) {
        throw new Error('No statement selected');
      }

      // Find the account containing this transaction
      const account = accounts.find(a => a.transactions.some(t => t.id === transactionId));

      if (!account) {
        throw new Error('Account not found for transaction');
      }

      onTransactionDelete?.(transactionId, account.id);
      showToast('Transaction deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      const errorMsg = getErrorMessage(error, 'Failed to delete transaction');
      showToast(errorMsg, 'error');
    }
  };

  const handleTransactionAdd = async (newTransaction) => {
    try {
      if (!addingInlineToAccountId || !onTransactionAdd) {
        return;
      }

      // Add the accountNumber to the transaction
      const transactionWithAccount = {
        ...newTransaction,
        accountNumber: addingInlineToAccountId.toString(),
        verificationStatus: VerificationStatus.Manual,
        lastVerifiedAt: new Date().toISOString(),
      };

      await onTransactionAdd(transactionWithAccount);

      setAddingInlineToAccountId(null);
    } catch (error) {
      console.error('Error adding transaction:', error);
      const errorMsg = getErrorMessage(error, 'Failed to add transaction');
      showToast(errorMsg, 'error');
    }
  };

  const handleAccountSave = async (
    accountId,
    field,
    value
  ) => {
    console.log('Starting handleAccountSave:', { accountId, field, value });
    setSavingFields({ accountId, field });

    try {
      const accountIndex = accounts.findIndex(a => a.id === accountId);
      console.log('Found account at index:', accountIndex);

      if (accountIndex === -1) throw new Error('Account not found');

      if (!value) throw new Error('A value is required');

      const account = accounts[accountIndex];
      console.log('Original account:', account);

      let processedValue = value;
      // Handle special value processing for different field types
      if (field.includes('Balance') && value) {
        processedValue = parseFloat(value).toString();
        console.log('Processed balance value:', processedValue);
      }

      // Reset reconciliation status if updating balance fields
      const shouldResetReconciliation = field.includes('Balance');

      const updatePayload = {
        ...account,
        [field]: processedValue,
        [`${field}Confidence`]: 1.0,
        // Reset reconciliation status if updating balance fields
        ...(shouldResetReconciliation && {
          isReconciled: false,
          lastReconciliationTime: undefined,
          lastReconciliationDifference: undefined,
        }),
      };
      console.log('Update payload:', updatePayload);

      await onAccountUpdate?.(accountId, updatePayload);

      // Update local state if server update was successful
      const updatedAccounts = [...accounts];
      updatedAccounts[accountIndex] = {
        ...account,
        [field]: processedValue,
        [`${field}Confidence`]: 1.0,
        // Reset reconciliation status if updating balance fields
        ...(shouldResetReconciliation && {
          isReconciled: false,
          lastReconciliationTime: undefined,
          lastReconciliationDifference: undefined,
        }),
      };

      // Update parent state
      if (selectedStatement && onStatementUpdate) {
        const updatedStatement = {
          ...selectedStatement,
          accounts: updatedAccounts,
        };
        onStatementUpdate(updatedStatement);
      }
    } catch (error) {
      console.error('Error in handleAccountSave:', error);
      const errorMsg = getErrorMessage(error, 'Failed to update account details');
      showToast(errorMsg, 'error');
    } finally {
      setSavingFields(null);
      setEditingAccountMeta(null);
    }
  };

  const validateTransactionBeforeVerification = (transaction) => {
    // Validate type first
    if (!transaction.type || transaction.type === 'Unknown') {
      return 'Transaction type must be either Deposit or Withdrawal';
    }

    // Validate date using existing validateDate function
    const dateError = validateDate(transaction.date);
    if (dateError) return dateError;

    // Validate amount based on transaction type
    if (transaction.type === 'Deposit') {
      if (!transaction.depositAmount) {
        return 'Deposit amount is required for Deposit type';
      }
      const amountError = validateAmount(transaction.depositAmount.toString());
      if (amountError) return amountError;
    } else if (transaction.type === 'Withdrawal') {
      if (!transaction.withdrawalAmount) {
        return 'Withdrawal amount is required for Withdrawal type';
      }
      const amountError = validateAmount(transaction.withdrawalAmount.toString());
      if (amountError) return amountError;
    }

    // Validation for description commented since it's readonly
    // if (!transaction.description || transaction.description.trim() === '') {
    //   return 'Transaction description is required';
    // }

    return null;
  };

  // Handle transaction verification
  const handleAccept = async (e, transaction) => {
    e.stopPropagation();

    // First, ensure the transaction has the correct type set
    const type = transaction.depositAmount
      ? 'Deposit'
      : transaction.withdrawalAmount
        ? 'Withdrawal'
        : transaction.type || 'Unknown';

    const transactionWithType = {
      ...transaction,
      type,
    };

    // Add validation
    const validationError = validateTransactionBeforeVerification(transactionWithType);
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }

    try {
      const updatedTransaction = {
        ...transactionWithType,
        depositAmount: transactionWithType.type === 'Deposit' ? transactionWithType.depositAmount : undefined,
        withdrawalAmount: transactionWithType.type === 'Withdrawal' ? transactionWithType.withdrawalAmount : undefined,
        date: format(getOrConvertToPst(transactionWithType.date), 'yyyy-MM-dd'),
        verificationStatus: VerificationStatus.Manual,
        lastVerifiedAt: new Date().toISOString(),
        dateConfidence: 1.0,
        descriptionConfidence: 1.0,
        depositAmountConfidence: transaction.depositAmount ? 1.0 : undefined,
        withdrawalAmountConfidence: transaction.withdrawalAmount
          ? 1.0
          : undefined,
      };

      await onTransactionUpdate?.(updatedTransaction, true);
    } catch (error) {
      console.error('Error verifying transaction:', error);
      const errorMsg = getErrorMessage(error, 'Failed to verify transaction');
      showToast(errorMsg, 'error');
    }
  };

  // Render action buttons for transactions
  const renderActionButtons = (transaction, accountId) => (
    <TransactionActions
      transaction={transaction}
      accountId={accountId}
      onDelete={handleTransactionDelete}
      onEdit={startEdit}
      onAccept={handleAccept}
      userRole={userRole}
    />
  );

  const renderSuccessMessage = () => {
    if (userRole === UserRole.User && transactionCounts.needsReview === 0) {
      return (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e5e7eb',
            boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 50,
            padding: '1.5rem',
            animation: 'slideUp 0.5s ease-out',
            transform: 'translateY(0)',
          }}
        >
          <style>
            {`
              @keyframes slideUp {
                from {
                  transform: translateY(100%);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <div
            style={{
              maxWidth: '1000px',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <CheckCircle2
              style={{
                width: '48px',
                height: '48px',
                color: '#10b981',
                animation: 'fadeIn 0.5s ease-out 0.3s both',
              }}
              strokeWidth={2}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                animation: 'fadeIn 0.5s ease-out 0.4s both',
              }}
            >
              <span
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#111827',
                  textAlign: 'center',
                }}
              >
                Looks good!
              </span>
              <span
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 500,
                  color: '#111827',
                  textAlign: 'center',
                }}
              >
                Your
                {' '}
                {transactionCounts.verified + transactionCounts.manuallyVerified}
                {' '}
                transactions from this statement has been successfully submitted for review
              </span>
              <button
                type="button"
                className="mt-2"
                onClick={onBack}
                style={headerStyles.backButton}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                  e.currentTarget.style.borderColor = '#D1D5DB';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M10 12L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Back to Statements
              </button>
            </div>
            <style>
              {`
                @keyframes fadeIn {
                  from {
                    opacity: 0;
                    transform: translateY(10px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
                @keyframes pulseHighlight {
                  0% { background-color: rgba(59, 130, 246, 0.1); }
                  50% { background-color: rgba(59, 130, 246, 0.15); }
                  100% { background-color: rgba(59, 130, 246, 0.1); }
                }
              `}
            </style>
          </div>
        </div>
      );
    }
    return null;
  };

  // Render feedback row for empty states
  const renderFeedbackRow = () => {
    if (
      (showNeedsReview && showVerified)
      || (showNeedsReview && transactionCounts.needsReview > 0)
      || (showVerified && transactionCounts.verified > 0)
    ) {
      console.log('Returning null due to condition check');
      return null;
    }

    if (
      transactionCounts.needsReview === 0
      && transactionCounts.verified === 0
    ) {
      return (
        <tr>
          <td colSpan={6} className="text-center py-12">
            <div className="flex flex-col items-center gap-6">
              <AlertTriangle
                className="w-12 h-12 text-amber-500"
                strokeWidth={2}
              />
              <span className="text-2xl font-medium text-gray-900">
                No Transactions Found
              </span>
              <div className="text-gray-600 text-center">
                <p>
                  Unfortunately, we were unable to extract transactions from
                  your bank statement.
                </p>
                <p>
                  Please upload a clearer or more legible statement for better
                  results.
                </p>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    if (
      showNeedsReview
      && !showVerified
      && transactionCounts.needsReview === 0
    ) {
      return (
        <tr>
          <td colSpan={6} className="text-center py-12">
            <div className="flex flex-col items-center gap-6">
              <CheckCircle2
                className="w-12 h-12 text-emerald-500"
                strokeWidth={2}
              />
              <span className="text-2xl font-medium text-gray-900">
                All Caught Up!
              </span>
              <span className="text-gray-600">
                All
                {' '}
                {transactionCounts.verified}
                {' '}
                transactions have been verified
                successfully.
              </span>
            </div>
          </td>
        </tr>
      );
    }

    return null;
  };

  const GuidanceTooltip = () => {
    if (!hoveredRow || userRole !== UserRole.User) return null;

    return (
      <div
        style={{
          position: 'fixed',
          background: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          zIndex: 50,
          // Change this line to move tooltip further left
          left: hoveredRow.rect ? `${hoveredRow.rect.left - 220}px` : 0,
          top: hoveredRow.rect
            ? `${hoveredRow.rect.top + hoveredRow.rect.height / 2}px`
            : 0,
          transform: 'translateY(-50%)',
          transition: 'opacity 0.2s ease',
          pointerEvents: 'none',
        }}
      >
        <ArrowLeft className="w-4 h-4 text-blue-600 mr-2" />
        <span className="text-xs font-medium text-blue-600">
          Click to view in statement
        </span>
      </div>
    );
  };

  const tableHeaderStyles = {
    backgroundColor: '#f8fafc',
    color: '#1e293b',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '12px 16px',
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    borderBottom: '2px solid #e2e8f0',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    whiteSpace: 'nowrap',
  };

  const onRowClick = (
    e,
    transactionId,
    accountId
  ) => {
    // Get the closest button to the click target
    const clickedButton = (e.target).closest('button');

    // If clicked element is a button or inside a button, don't handle the row click
    if (clickedButton) {
      return;
    }

    const field = e.target.closest('td')?.getAttribute('data-field') || null;
    if (onTransactionSelect && !editingAccountMeta) {
      onTransactionSelect?.(transactionId, accountId.toString());

      if (transactionId === selectedTransactionId && selectedAccountId === accountId.toString()) {
        const transaction = accounts.find(a => a.id === accountId)?.transactions.find(t => t.id === transactionId);

        if (userRole === UserRole.User && transaction.verificationStatus !== VerificationStatus.NeedsReview) {
          return;
        }
        startEdit(e, transactionId, accountId, field);
      }
    }
  };

    const sortTransactions = (a, b) => {
    let aValue;
    let bValue;

    if (sortConfig.column === 'type') {
      aValue = a.type || (a.depositAmount ? 'Deposit' : a.withdrawalAmount ? 'Withdrawal' : 'Unknown');
      bValue = b.type || (b.depositAmount ? 'Deposit' : b.withdrawalAmount ? 'Withdrawal' : 'Unknown');
    } else if (sortConfig.column === 'amount') {
      aValue = a.depositAmount || -a.withdrawalAmount || 0;
      bValue = b.depositAmount || -b.withdrawalAmount || 0;
    } else if (sortConfig.column === 'status') {
      const statusOrder = {
        NeedsReview: 1,
        Manual: 2,
        Auto: 3,
      };
      aValue = statusOrder[a.verificationStatus] || 0;
      bValue = statusOrder[b.verificationStatus] || 0;
    } else {
      aValue = a[sortConfig.column];
      bValue = b[sortConfig.column];
    }

    if (aValue == null || bValue == null) return 0;

    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    }
    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div className="table-container relative">
      <table className="transactions-table">
        <thead>
          <tr className="table-header-row">
            <th style={{ ...tableHeaderStyles, width: '14%' }}>
              <div className="w-100 flex items-center justify-between">
                Date
                <button type="button" onClick={() => handleSort('date')}>
                  {sortConfig.column === 'date' && sortConfig.direction === 'asc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </th>
            <th style={{ ...tableHeaderStyles, width: '11%' }}>
              <div className="w-100 flex items-center justify-between">
                Type
                <button type="button" onClick={() => handleSort('type')}>
                  {sortConfig.column === 'type' && sortConfig.direction === 'asc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </th>
            <th
              style={{ ...tableHeaderStyles, width: '8%', textAlign: 'right' }}
            >
              <div className="w-100 flex items-center justify-between">
                Amount
                <button type="button" onClick={() => handleSort('amount')}>
                  {sortConfig.column === 'amount' && sortConfig.direction === 'asc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </th>
            <th style={{ ...tableHeaderStyles, width: '46%' }}>
              <div className="w-100 flex items-center justify-between">
                Description
                <button type="button" onClick={() => handleSort('description')}>
                  {sortConfig.column === 'description' && sortConfig.direction === 'asc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </th>
            <th
              style={{
                ...tableHeaderStyles,
                width: '12%',
                textAlign: 'center',
              }}
            >
              <div className="w-100 flex items-center justify-between">
                Status
                <button type="button" onClick={() => handleSort('status')}>
                  {sortConfig.column === 'status' && sortConfig.direction === 'asc' ? <ArrowDown className="h-4 w-4" /> : <ArrowUp className="h-4 w-4" />}
                </button>
              </div>
            </th>
            <th
              style={{
                ...tableHeaderStyles,
                width: '11%',
                textAlign: 'center',
              }}
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.length > 0
            ? accounts.map((account, accountIndex) => (
              <Fragment key={`account_${account.id}_${accountIndex}`}>
                {selectedStatement && (
                  <AccountHeader
                    account={account}
                    editingField={editingAccountMeta && editingAccountMeta.accountId === account.id ? editingAccountMeta.field : null}
                    onEdit={field => handleAccountEdit(account.id, field)}
                    onSave={(field, value) => handleAccountSave(account.id, field, value)}
                    onCancel={() => setEditingAccountMeta(null)}
                    savingFields={savingFields && savingFields.accountId === account.id ? savingFields : null}
                    onAddTransaction={handleAddTransactionClick}
                    statementId={statementId}
                  />
                )}

                {addingInlineToAccountId === account.id && (
                  <InlineTransactionForm
                    key={`inline-form-${account.id}`}
                    accountId={account.id}
                    onCancel={() => {
                      console.log(
                        'Cancelling form for account:',
                        account.id
                      );
                      setAddingInlineToAccountId(null);
                    }}
                    onAdd={handleTransactionAdd}
                    statement={selectedStatement}
                  />
                )}

                {account.transactions
                  .filter((transaction) => {
                    if (
                      !showNeedsReview
                      && !showVerified
                      && !showManuallyVerified
                    ) {
                      return true;
                    }
                    if (
                      showNeedsReview
                      && transaction.verificationStatus === VerificationStatus.NeedsReview
                    ) {
                      return true;
                    }
                    if (
                      showVerified
                      && transaction.verificationStatus === VerificationStatus.Auto
                    ) {
                      return true;
                    }
                    if (
                      showManuallyVerified
                      && transaction.verificationStatus === VerificationStatus.Manual
                    ) {
                      return true;
                    }
                    return false;
                  })
                  .sort(sortTransactions)
                  .map((transaction) => {
                    if (
                      editingTransactionMeta
                      && editingTransactionMeta.accountId === account.id
                      && editingTransactionMeta.transactionId === transaction.id
                    ) {
                      return (
                        <TransactionEditor
                          key={`${transaction.id}`}
                          transaction={{
                            ...transaction,
                            accountNumber: account.accountNumber,
                          }}
                          statementId={statementId}
                          onCancel={() => cancelEdit()}
                          onSave={handleTransactionSave}
                          userRole={userRole}
                          focusField={editingTransactionMeta?.field}
                        />
                      );
                    }

                    return (
                      <tr
                        key={`${transaction.id}`}
                        className={`cursor-pointer transition-all duration-200 relative
                        ${
                          selectedTransactionId === transaction.id
                          && selectedAccountId === account.id.toString()
                            ? 'selected'
                            : ''
                        } 
                        ${
                          accountIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }
                        ${
                          userRole === UserRole.User
                          && transaction.verificationStatus === VerificationStatus.NeedsReview
                            ? 'hover:bg-blue-50'
                            : ''
                        }`}
                        onMouseEnter={(e) => {
                          if (
                            userRole === UserRole.User
                            && transaction.verificationStatus === VerificationStatus.NeedsReview
                          ) {
                            if (tooltipTimeoutRef.current) {
                              clearTimeout(tooltipTimeoutRef.current);
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoveredRow({ id: transaction.id, rect });
                          }
                        }}
                        onMouseLeave={() => {
                          if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
                          tooltipTimeoutRef.current = setTimeout(() => {
                            setHoveredRow(null);
                          }, 1000);
                        }}
                        onClick={(e) => {
                          const { target } = e;
                          if (
                            !target.closest('button')
                            && !target.closest('.action-cell')
                          ) {
                            onRowClick(e, transaction.id, account.id);
                          }
                        }}
                      >
                        <td data-field="date">{formatDate(transaction.date)}</td>
                        <td data-field="type">
                          {transaction.type
                          || (transaction.depositAmount
                            ? 'Deposit'
                            : transaction.withdrawalAmount
                              ? 'Withdrawal'
                              : 'Unknown')}
                        </td>
                        <td data-field="amount" className="text-right">
                          {transaction.depositAmount
                            ? `$${transaction.depositAmount.toFixed(2)}`
                            : transaction.withdrawalAmount
                              ? `-$${transaction.withdrawalAmount.toFixed(2)}`
                              : '-'}
                        </td>
                        <td data-field="description">{transaction.description}</td>
                        <td data-field="status" className="text-center">
                          <span
                            className={`confidence-badge ${
                              transaction.verificationStatus === VerificationStatus.NeedsReview
                                ? 'review'
                                : transaction.verificationStatus === VerificationStatus.Manual
                                  ? 'manual'
                                  : 'verified'
                            }`}
                          >
                            {transaction.verificationStatus === VerificationStatus.NeedsReview
                              ? 'Needs Review'
                              : transaction.verificationStatus === VerificationStatus.Manual
                                ? 'Manual'
                                : 'Auto'}
                          </span>
                        </td>
                        <td data-field="actions" className="text-center action-cell">
                          {renderActionButtons(transaction, account.id)}
                        </td>
                      </tr>
                    );
                  })}

                {accountIndex < accounts.length - 1 && (
                  <tr className="account-spacer">
                    <td
                      colSpan={6}
                      className="h-4 bg-white border-b border-gray-100"
                    />
                  </tr>
                )}
              </Fragment>
            ))
            : renderFeedbackRow()}
        </tbody>
      </table>
      {renderSuccessMessage()}
      <GuidanceTooltip />
    </div>
  );
};

TransactionsTable.propTypes = {
  accounts: PropTypes.array.isRequired,
  statementId: PropTypes.string.isRequired,
  selectedStatement: PropTypes.object,
  onTransactionSelect: PropTypes.func,
  selectedTransactionId: PropTypes.number,
  selectedAccountId: PropTypes.string,
  onTransactionUpdate: PropTypes.func,
  onAccountUpdate: PropTypes.func,
  onStatementUpdate: PropTypes.func,
  onTransactionDelete: PropTypes.func,
  onTransactionAdd: PropTypes.func,
  transactionCounts: PropTypes.object.isRequired,
  showNeedsReview: PropTypes.bool.isRequired,
  showVerified: PropTypes.bool.isRequired,
  showManuallyVerified: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default TransactionsTable;
